import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';

export interface CustomFieldDefinition {
  id: string;
  organization_id: string;
  name: string;
  field_type: 'text' | 'number' | 'date' | 'dropdown' | 'textarea' | 'checkbox';
  is_required: boolean;
  default_value: string | null;
  options: any; // JSONB - array for dropdown fields
  validation_rules: any; // JSONB
  display_order: number;
  is_active: boolean;
  category_id: string | null; // Deprecated - use category_ids from junction table
  created_at: string;
  updated_at: string;
  // Populated from junction table - empty array means applies to ALL categories
  category_ids?: string[];
}

export interface CustomFieldValue {
  id: string;
  asset_id: string;
  custom_field_id: string;
  value: string;
  created_at: string;
  updated_at: string;
}

interface CustomFieldFormData {
  name: string;
  field_type: 'text' | 'number' | 'date' | 'dropdown' | 'textarea' | 'checkbox';
  is_required: boolean;
  default_value?: string | null;
  options?: any;
  display_order: number;
  is_active: boolean;
  category_id?: string | null;
}

// Query hooks for field definitions
export function useCustomFieldDefinitions(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['custom-field-definitions', organizationId],
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID is required');

      // Fetch custom fields
      const { data: fields, error } = await supabase
        .from('custom_fields')
        .select('*')
        .eq('organization_id', organizationId)
        .order('display_order', { ascending: true });

      if (error) throw error;

      // Fetch category associations for all fields
      const fieldIds = (fields || []).map((f: any) => f.id);
      const { data: associations } = await supabase
        .from('category_custom_field_definitions')
        .select('custom_field_definition_id, category_id')
        .in('custom_field_definition_id', fieldIds);

      // Build a map of field_id -> category_ids
      const categoryMap = new Map<string, string[]>();
      (associations || []).forEach((a: any) => {
        const existing = categoryMap.get(a.custom_field_definition_id) || [];
        existing.push(a.category_id);
        categoryMap.set(a.custom_field_definition_id, existing);
      });

      // Attach category_ids to each field (empty array = applies to all)
      return (fields || []).map((field: any) => ({
        ...field,
        category_ids: categoryMap.get(field.id) || [],
      })) as CustomFieldDefinition[];
    },
    enabled: !!organizationId,
  });
}

export function useCustomFieldDefinition(fieldId: string | undefined) {
  return useQuery({
    queryKey: ['custom-field-definitions', fieldId],
    queryFn: async () => {
      if (!fieldId) throw new Error('Field ID is required');

      const { data, error } = await supabase
        .from('custom_fields')
        .select('*')
        .eq('id', fieldId)
        .single();

      if (error) throw error;
      return data as CustomFieldDefinition;
    },
    enabled: !!fieldId,
  });
}

// Query hooks for field values
export function useCustomFieldValues(assetId: string | undefined) {
  return useQuery({
    queryKey: ['custom-field-values', assetId],
    queryFn: async () => {
      if (!assetId) throw new Error('Asset ID is required');

      const { data, error } = await supabase
        .from('asset_custom_values')
        .select('*')
        .eq('asset_id', assetId);

      if (error) throw error;
      return data as CustomFieldValue[];
    },
    enabled: !!assetId,
  });
}

// Mutation hooks for field definitions
export function useCreateCustomFieldDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CustomFieldFormData & { organization_id: string }) => {
      const { error, data: newField } = await supabase
        .from('custom_fields')
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return newField;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-field-definitions'] });
      notifications.show({
        title: 'Success',
        message: 'Custom field created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create custom field',
        color: 'red',
      });
    },
  });
}

export function useUpdateCustomFieldDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CustomFieldFormData> }) => {
      const { error, data: updatedField } = await supabase
        .from('custom_fields')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return updatedField;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-field-definitions'] });
      notifications.show({
        title: 'Success',
        message: 'Custom field updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update custom field',
        color: 'red',
      });
    },
  });
}

export function useDeleteCustomFieldDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fieldId: string) => {
      const { error } = await supabase
        .from('custom_fields')
        .delete()
        .eq('id', fieldId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-field-definitions'] });
      notifications.show({
        title: 'Success',
        message: 'Custom field deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete custom field',
        color: 'red',
      });
    },
  });
}

// Mutation hooks for field values
export function useSaveCustomFieldValues() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      assetId,
      organizationId,
      values,
    }: {
      assetId: string;
      organizationId: string;
      values: { field_definition_id: string; value: string }[];
    }) => {
      // Delete existing values for this asset
      await supabase.from('asset_custom_values').delete().eq('asset_id', assetId);

      // Insert new values
      if (values.length > 0) {
        const { error } = await supabase.from('asset_custom_values').insert(
          values.map((v) => ({
            asset_id: assetId,
            custom_field_id: v.field_definition_id,
            value: v.value,
          }))
        );

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-field-values'] });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to save custom field values',
        color: 'red',
      });
    },
  });
}

// Hook to get custom fields for a specific category
// Returns { universal: fields for all categories, categorySpecific: fields for this category }
export function useCustomFieldsForCategory(organizationId: string | undefined, categoryId: string | null) {
  return useQuery({
    queryKey: ['custom-field-definitions', 'for-category', categoryId, organizationId],
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID is required');

      // Fetch all active custom fields
      const { data: allFields, error } = await supabase
        .from('custom_fields')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;

      // Fetch all category associations
      const fieldIds = (allFields || []).map((f: any) => f.id);
      const { data: associations } = await supabase
        .from('category_custom_field_definitions')
        .select('custom_field_definition_id, category_id')
        .in('custom_field_definition_id', fieldIds);

      // Build map of field_id -> category_ids
      const categoryMap = new Map<string, string[]>();
      (associations || []).forEach((a: any) => {
        const existing = categoryMap.get(a.custom_field_definition_id) || [];
        existing.push(a.category_id);
        categoryMap.set(a.custom_field_definition_id, existing);
      });

      // Separate fields into universal (no categories = all) and category-specific
      const universal: CustomFieldDefinition[] = [];
      const categorySpecific: CustomFieldDefinition[] = [];

      (allFields || []).forEach((field: any) => {
        const fieldCategoryIds = categoryMap.get(field.id) || [];
        const fieldWithCategories = { ...field, category_ids: fieldCategoryIds };

        if (fieldCategoryIds.length === 0) {
          // No categories assigned = applies to ALL categories
          universal.push(fieldWithCategories);
        } else if (categoryId && fieldCategoryIds.includes(categoryId)) {
          // Has categories and current category is in the list
          categorySpecific.push(fieldWithCategories);
        }
        // If field has categories but current category isn't in list, skip it
      });

      return { universal, categorySpecific };
    },
    enabled: !!organizationId,
  });
}

// Mutation hook to assign categories to a custom field
export function useAssignCategoriesToCustomField() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fieldId, categoryIds }: { fieldId: string; categoryIds: string[] }) => {
      // Delete existing category assignments
      await supabase
        .from('category_custom_field_definitions')
        .delete()
        .eq('custom_field_definition_id', fieldId);

      // Insert new category assignments
      if (categoryIds.length > 0) {
        const { error } = await supabase
          .from('category_custom_field_definitions')
          .insert(
            categoryIds.map((categoryId) => ({
              custom_field_definition_id: fieldId,
              category_id: categoryId,
            }))
          );

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-field-definitions'] });
      notifications.show({
        title: 'Success',
        message: 'Category assignments updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update category assignments',
        color: 'red',
      });
    },
  });
}
